// Formatting + status helpers shared across
// Kanban, table, archive, and dashboard widgets.

import type { DealListItemDto, DealPipelineStage, DealSubState } from "@/types/deals";
import { slaTargetFor } from "@/api/dealLookups";

const USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "—";
  return USD.format(amount);
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export function daysSince(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / MS_PER_DAY));
}

/** Compact age label that rolls days → weeks → months so cards don't sit
 *  at "37d" forever. <7 days renders as Nd, <30 days as Nw, otherwise Nm.
 *  Single source of truth for the table badges (the Kanban card shows
 *  formatSlaBadge). */
export function formatStageAge(iso: string | null | undefined): string {
  const days = daysSince(iso);
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}m`;
}

export type AgeSeverity = "ok" | "warning" | "breach";

/** Returns the SLA severity for a deal's age in its current sub-state. */
export function ageSeverity(deal: DealListItemDto): AgeSeverity {
  const days = daysSince(deal.stageEnteredDate);
  const target = slaTargetFor(deal.stage, deal.subState);
  if (!target) return "ok";
  if (days >= target) return "breach";
  if (days >= target * 0.8) return "warning";
  return "ok";
}

export function ageBadgeStatus(severity: AgeSeverity): "draft" | "review" | "error" {
  if (severity === "breach") return "error";
  if (severity === "warning") return "review";
  return "draft";
}

/** Compact SLA badge text for the Kanban deal card. The sub-state name is
 *  already implied by the column header — repeating it inside the card
 *  overflows the 320px column. Returns a short delta + "overdue" indicator
 *  when past target. The ⚠ glyph for warning is rendered separately as a
 *  Lucide icon by the card so the cell stays text-only here. */
export function formatSlaBadge(deal: DealListItemDto): string {
  const days = daysSince(deal.stageEnteredDate);
  if (ageSeverity(deal) === "breach") return `${days}d overdue`;
  return `${days}d`;
}

export function dealLabel(deal: DealListItemDto): string {
  return deal.displayName?.trim() || deal.businessNameInitial?.trim() || `Deal — ${deal.dealNumber}`;
}

export function dealBusinessLabel(deal: DealListItemDto): string {
  return deal.businessNameInitial?.trim() || dealLabel(deal);
}

/** Heuristic readiness signal — green dot when the sub-state name implies
 *  the deal is one drag away from advancing. The backend still validates
 *  every transition; this is purely a visual nudge. */
export function isReadyToAdvance(subState: DealSubState): boolean {
  return (
    subState === "ReadyForAssignment" ||
    subState === "ReadyToSubmit"
  );
}

/** Per-stage Tailwind accent classes for column headers. Uses semantic
 *  tokens (status palette + gold) instead of raw hex. */
export const STAGE_ACCENT: Record<DealPipelineStage, { dot: string; text: string; ring: string }> = {
  Intake: { dot: "bg-status-info", text: "text-status-info", ring: "ring-status-info/30" },
  PreApproval: { dot: "bg-gold", text: "text-gold-shadow", ring: "ring-gold/30" },
  Underwriting: { dot: "bg-status-warning", text: "text-status-warning", ring: "ring-status-warning/30" },
  Closing: { dot: "bg-status-success", text: "text-status-success", ring: "ring-status-success/30" },
  Closed: { dot: "bg-onyx", text: "text-onyx", ring: "ring-onyx/30" },
  Declined: { dot: "bg-status-error", text: "text-status-error", ring: "ring-status-error/30" },
  Withdrawn: { dot: "bg-gray-mid", text: "text-gray-mid", ring: "ring-gray-mid/30" },
};
